import { createFilterParams } from "Utils/searchFilters";
import API from "Services/Api";
import { Moment } from "moment";

export type Multiples = { id: number; status: string }[];

export type Filters = {
  search: string;
  page: number;
  start_date: Moment | undefined;
  end_date: Moment | undefined;
  status_id: number[] | null;
  client_id: number[] | null;
  state: string;
};

type FiltersReq = Omit<Filters, "start_date" | "end_date" | "per_page"> & {
  start_date: string | undefined;
  end_date: string | undefined;
  per_page: string;
};

export type InsuranceCardUploadMetadata = {
  file_name: string;
  file_size: number;
  mime_type: string;
};

export interface SavePreBudgetData {
  cart: {
    product_id: number;
    amount: number;
  }[];
  patient: {
    full_name: string;
    email: string;
    phone: string;
    doctor: string;
    crm: string;
    document: string;
    ddd: string;
    ddi: string;
    mothers_full_name?: string;
    birth_date?: string;
    gender?: string;
    insurance_operator?: string | null;
    health_insurance_card_number?: string | null;
    have_health_insurance?: string | null;
  };
  insurance_card_upload?: InsuranceCardUploadMetadata;
  track:
    | {
        produto_original: {
          produto_original_nome: string;
          produto_original_match_nome: string;
          produto_original_match_id: number | null;
        };
        items_corretos: any[];
      }[]
    | {
        exams_normalized: string[];
        items: {
          original_product: {
            original_name: string;
            found_name: string;
            id: number;
            details: {
              method: string;
              confidence: number;
            };
          };
          final_status: "kept" | "replaced" | "removed";
          final_products?: {
            id: number;
            name: string;
          }[];
        }[];
        added_products: {
          id: number;
          name: string;
        }[];
        not_founded: string[];
      };
}

export type UpdatePreOrderUnitData = SavePreBudgetData & {
  unit_id: number;
};

export type UpdatePreOrderUnitCartData = {
  cart: {
    product_id: number;
    amount: number;
    source: "ia" | "manual";
  }[];
};
type Address = {
  city: string;
  complement: string;
  state: string;
  number: string;
  street: string;
  zipcode: string;
  neighborhood: string;
};

export type Client = {
  id: number;
  name: string;
  appointments: { id: number; name: string }[];
  pre_flow: boolean;
};

type Status = {
  id: number;
  status: string;
};

export type PreOrderList = {
  acuity_id: number | null;
  doctor: string | null;
  crm: string | null;
  products: {
    id: number;
    product: string;
    product_id: number;
    sku: string;
    amount: number;
    price: number;
    source?: string;
  }[];
  hasToPay: boolean;
  created_at: string;
  appointment_at: string | null;
  birth_date: string;
  budget_id: null | number;
  client: Client;
  ddd: string;
  ddi: string;
  document: string;
  email: string;
  files: { file_path: string }[];
  full_name: string;
  gender: string;
  height: string;
  mothers_full_name: string;
  id: number;
  observation: string;
  person_id: null | number;
  order_id: null | number;
  phone: string;
  status: Status;
  symptoms_start_date: string | null;
  weight: string;
  locked_at: string;
  locked_by: number;
  track?: TrackList; // Campo opcional para o track da API
  unit_id?: number;
  have_health_insurance: null | string;
  health_insurance_card_number: null | string;
};
export type PreOrderDetail = PreOrderList & {
  address: Address;
};

type PersonUpdate = {
  document: string;
  ddi: string;
  ddd: string;
  phone: string;
  doctor?: string;
  crm?: string;
};

type UpdatePersonalInfoPreBudget = PersonUpdate | Address;

type PreOrderListAPI = {
  current_page: number;
  data: PreOrderList[];
  last_page: number;
  per_page: string;
  total: number;
};

type PreOrdersCombos = {
  clients: {
    id: number;
    name: string;
  }[];
  dashboards: {
    total: number;
    pending: number;
    confirmed: number;
    partial: number;
    cancelled: number;
  };
  status: Status[];
  status_pre_orders: Status[];
};

export type ProductAdded = {
  amount: number;
  product_id: number;
  product_name: string;
  product_code: string;
  product_price: number;
};

type CompleteOrder = {
  count: number;
  errors: [];
  items: ProductAdded[];
  items_pre_order: number;
};

type TrackList = {
  produto_original: {
    produto_original_nome: string;
    produto_original_match_nome: string;
    produto_original_match_id: null | number;
  };
  items_corretos: {
    produto_corrigido_nome: string;
    produto_corrigido_id: string | number; // Aceita tanto string quanto number
  }[];
}[];

type Cart = {
  cart: {
    product_id: number;
    amount: number;
  }[];
  patient: {
    full_name: string;
    email: string;
    phone: string;
    ddi: string;
    ddd: string;
    doctor: string;
    crm: string;
    address: {
      zipcode: string;
      street: string;
      number: string;
    } | null;
  };
  track: null | TrackList;
};

// ROTAS DE ORÇAMENTO PARCEIROS
export const listAllPreBudgets = async (
  filters: FiltersReq
): Promise<PreOrderListAPI> => {
  const response = await API.get("/clients/pre-budgets", {
    params: createFilterParams(filters),
  });
  return response.data;
};

export const preBudgetCombos = async (): Promise<PreOrdersCombos> => {
  const response = await API.get("/clients/pre-budgets/combos");
  return response.data;
};

export const showBudgetOrderDetails = async (
  preOrderId: number
): Promise<PreOrderDetail> => {
  try {
    const response = await API.get(`clients/pre-budgets/${preOrderId}`);
    return response.data;
  } catch (error: any) {
    throw error.response.data;
  }
};

// converter pre-orçamento em orçamento
export const completePreBudget = async (preOrderId: number) => {
  try {
    const response = await API.post(`/clients/budgets`, {
      id: preOrderId,
    });
    return response;
  } catch (error: any) {
    throw error.response.data;
  }
};

// SALVAR ALTERAÇÕES DE PESSOAS NO CARRINHO
export const updatePersonalInfoPreBudget = async (
  id: number,
  data: UpdatePersonalInfoPreBudget
) => {
  const response = await API.put(`/clients/pre-budgets/${id}/person`, {
    ...data,
  });
  return response.data;
};

// salvar itens no carrinho.
export const saveCartChanges = async (
  preBudgetId: number,
  cart: Cart
): Promise<CompleteOrder> => {
  const response = await API.put(`/clients/pre-budgets/${preBudgetId}`, cart);
  return response.data;
};

// NOVA FUNÇÃO: Salvar alterações do pré-orçamento com IA

export const savePreBudgetChanges = async (
  preBudgetId: number,
  data: SavePreBudgetData
) => {
  const response = await API.put(`/clients/pre-budgets/${preBudgetId}`, data);
  return response.data;
};

export const cancelPreBudget = async (preBudgetId: number) => {
  const response = await API.delete(`/clients/pre-budgets/${preBudgetId}`);
  return response.data;
};

// Buscar arquivo de reivindicação médica
export const getMedicalClaimFile = async (url: string): Promise<Blob> => {
  try {
    const response = await API.get(
      `/medical-claim-file?url=${encodeURIComponent(url)}`,
      {
        responseType: "blob",
      }
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error;
  }
};

export const getMedicalClaimFileUnit = async (url: string): Promise<Blob> => {
  try {
    const response = await API.get(
      `/medical-claim-file/pre-order?path=${encodeURIComponent(url)}`,
      {
        responseType: "blob",
      }
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error;
  }
};

// INICIO ROTAS PARCEIROS UNIDADE

export const listAllPreBudgetsUnit = async (
  filters: FiltersReq
): Promise<PreOrderListAPI> => {
  const response = await API.get("/clients/units/pre-budgets", {
    params: createFilterParams(filters),
  });
  return response.data;
};

export const showPreBudgetUnitDetails = async (
  preOrderId: number
): Promise<PreOrderDetail> => {
  try {
    const response = await API.get(`clients/units/pre-budgets/${preOrderId}`);
    return response.data;
  } catch (error: any) {
    throw error.response.data;
  }
};

export const cancelPreBudgetUnit = async (preBudgetId: number) => {
  const response = await API.delete(
    `/clients/units/pre-budgets/${preBudgetId}`
  );
  return response.data;
};

export const deletePreBudgetUnit = async (preBudgetId: number) => {
  const response = await API.delete(
    `/clients/units/send-to-motion/${preBudgetId}`
  );
  return response.data;
};

export const savePreBudgetUnitChanges = async (
  preBudgetId: number,
  data: SavePreBudgetData
) => {
  const response = await API.put(
    `/clients/units/pre-budgets/${preBudgetId}`,
    data
  );
  return response.data;
};

export const saveCartChangesUnit = async (
  preBudgetId: number,
  cart: Cart
): Promise<CompleteOrder> => {
  const response = await API.put(
    `/clients/units/pre-budgets/${preBudgetId}`,
    cart
  );
  return response.data;
};

export const saveCartChangesUnitAI = async (
  preBudgetId: number,
  data: SavePreBudgetData
): Promise<CompleteOrder> => {
  const response = await API.put(
    `/clients/units/pre-budgets/${preBudgetId}`,
    data
  );
  return response.data;
};

export const updatePreOrderUnit = async (
  preBudgetId: number,
  data: UpdatePreOrderUnitData
): Promise<CompleteOrder> => {
  const response = await API.put(
    `/clients/units/pre-orders/${preBudgetId}`,
    data
  );
  return response.data;
};

export const sendPreOrderToMotion = async (preBudgetId: number) => {
  try {
    const response = await API.post("/clients/units/send-to-motion", {
      id: preBudgetId,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updatePreOrderUnitCart = async (
  preBudgetId: number,
  data: UpdatePreOrderUnitCartData
): Promise<CompleteOrder> => {
  const response = await API.put(
    `/clients/units/pre-orders/${preBudgetId}`,
    data
  );
  return response.data;
};
